import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, nullableString, requiredEmail, requiredString } from "common";
import React, { useMemo } from "react";
import { useMutation } from "react-query";
import { matchPath, useHistory, useParams } from "react-router-dom";
import { Except } from "type-fest";
import { Event, PurchaseItem, Traveler } from "types";
import { object, string } from "yup";
import { api } from "../../../api/api";
import { queryClient } from "../../../api/queryClient";
import { populateResource } from "../../../hooks/helper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { getEventIdFromTimeslotId } from "@/models/Timeslot";

const getValidationSchema = (isEmailRequired: boolean) => {
    return object().shape({
        name: requiredString(),
        phone: nullableString(),
        email: isEmailRequired ? requiredEmail() : string().trim().email(),
    });
};

interface EditPrimaryContactModalProps {
    contact: Traveler;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (values: any) => void;
    onDuplicate?: (values: EditedFormValues) => void;
}

export const EditPrimaryContactModal = ({
    contact,
    isOpen,
    onClose,
    onSuccess,
    onDuplicate,
}: EditPrimaryContactModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent contact={contact} onClose={onClose} onSuccess={onSuccess} onDuplicate={onDuplicate} />
        </Modal>
    );
};

export interface EditedFormValues {
    name: string;
    phone: string;
    email?: string;
}

const initialValues: EditedFormValues = {
    name: "",
    phone: "",
    email: "",
};

const ModalContent = ({ contact, onClose, onSuccess, onDuplicate }: Except<EditPrimaryContactModalProps, "isOpen">) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const history = useHistory();
    const initialValues = getFormValues(contact);
    const path = history.location.pathname;
    const { timeslotId } = useParams<{ timeslotId?: string }>();
    const isPurchasePage = useMemo(() => path.startsWith("/purchases"), [path]);
    const isDashboardRosterPage = useMemo(
        () =>
            matchPath(path, {
                path: "/dashboard/:id",
                exact: true,
                strict: false,
            }),
        [path],
    );

    const { mutateAsync: updateContact, isLoading } = useMutation(
        async (formValues: EditedFormValues) => {
            return api.put(`users/${contact.id}`, { ...formValues, seller: seller.id });
        },
        {
            onMutate: (formValues: EditedFormValues) => {
                const newContact = { ...contact, ...formValues };
                populateResource("users", newContact);
                queryClient.setQueryData(["users", contact.id, { private: true }], newContact);
                onClose();
            },
        },
    );

    const handleSubmit = async (values: EditedFormValues) => {
        try {
            if (!values.email && !initialValues.email) {
                delete values.email;
            }

            await updateContact(values);
            invalidate("users", contact.id);
            invalidate("purchases");
            if (isPurchasePage) {
                const purchaseId = getPurchaseIdFromUrl(history.location.pathname);
                if (purchaseId) {
                    invalidate("purchases", purchaseId);
                }
            }

            if (isDashboardRosterPage && timeslotId) {
                const eventId = getEventIdFromTimeslotId(timeslotId);
                const event = eventId ? queryClient.getQueryData<Event>(["events", eventId]) : undefined;
                const purchaseItems = event?.purchaseItems?.map((item) => item.id) ?? [];
                for (const purchaseItemId of purchaseItems) {
                    const purchaseItem = queryClient.getQueryData<PurchaseItem>(["purchaseItems", purchaseItemId]);
                    if (purchaseItem && purchaseItem.organizer?.user?.id === contact.id) {
                        invalidate("purchaseItems", purchaseItemId);
                    }
                }
            }

            onSuccess?.({ id: contact.id, ...values });
            flash.success("Contact details updated");
        } catch (error: any) {
            if (error.response.status === 409) {
                onDuplicate?.(values);
            }
        }
    };

    return (
        <XolaFormik
            validateOnBlur={false}
            validationSchema={getValidationSchema(!!initialValues.email)}
            initialValues={getFormValues(contact)}
            onReset={onClose}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit }) => (
                <>
                    <Modal.Header>Edit Primary Contact</Modal.Header>
                    <Modal.Body className="flex-col">
                        <fieldset className="flex-1 space-y-4" disabled={isLoading}>
                            <FormField name="name" label="Name">
                                <InputField isRequired />
                            </FormField>
                            <FormField name="email" label="Email">
                                <InputField isRequired={!!values.email} />
                            </FormField>
                            <FormField name="phone" label="Phone">
                                <InputField />
                            </FormField>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer className="flex justify-end space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const getFormValues = (contact: Traveler): EditedFormValues => {
    return {
        ...initialValues,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
    };
};

function getPurchaseIdFromUrl(url: string) {
    const match = /\/purchases\/([^/]+)/.exec(url);
    return match ? match[1] : null;
}
